import type { PortalFeatureFlags } from "./types.js";

export interface PortalNavItem {
  id: string;
  label: string;
  href: string;
  active?: boolean;
  restricted?: boolean;
}

export type PortalPage = "overview" | "map" | "leaderboards" | "skins" | "garage" | "store" | "marketplace" | "gambling" | "events" | "support" | "cases" | "staff" | "senior" | "community" | "technical" | "owner" | "developer";

export function buildPortalNavigation(
  flags: PortalFeatureFlags,
  activePage: PortalPage = "overview",
  permissions: string[] = []
): PortalNavItem[] {
  const base: PortalNavItem[] = [
    { id: "overview", label: "Live Dinosaur", href: "/portal/", active: activePage === "overview" },
    { id: "map", label: "Live Map", href: "/portal/map", active: activePage === "map" },
    { id: "leaderboards", label: "Hall of Records", href: "/portal/leaderboards", active: activePage === "leaderboards" },
    { id: "skins", label: "Skins", href: "/portal/skins", active: activePage === "skins" },
    { id: "garage", label: "Garage", href: "/portal/garage", active: activePage === "garage" },
    { id: "store", label: "Store", href: "/portal/store", active: activePage === "store" },
    { id: "marketplace", label: "Marketplace", href: "/portal/marketplace", active: activePage === "marketplace" },
    { id: "gambling", label: "Gambling", href: "/portal/gambling", active: activePage === "gambling" || activePage === "events" },
    { id: "support", label: "Support", href: "/portal/support", active: activePage === "support" }
  ];

  if (canViewCaseDesk(permissions)) {
    base.push({ id: "cases", label: "Case desk", href: "/portal/cases", active: activePage === "cases", restricted: true });
  }
  if (hasStaffPortalAccess(flags)) {
    base.push({ id: "staff", label: "Mod panel", href: "/portal/staff", active: activePage === "staff", restricted: true });
  }
  if (flags.showCommunityTools) {
    base.push({ id: "community", label: "Community Admin", href: "/portal/community", active: activePage === "community", restricted: true });
  }
  if (flags.showSeniorTools) {
    base.push({ id: "senior", label: "Senior Mod & Admin", href: "/portal/senior", active: activePage === "senior", restricted: true });
  }
  if (flags.showTechnicalTools) {
    base.push({ id: "technical", label: "Technical Operations", href: "/portal/technical", active: activePage === "technical", restricted: true });
  }
  if (flags.showOwnerTools) {
    base.push({ id: "owner", label: flags.showDeveloperTools ? "Owner & Development" : "Owner panel", href: "/portal/config", active: activePage === "owner", restricted: true });
  }

  return base;
}

export function canViewCaseDesk(permissions: string[]): boolean {
  return permissions.includes("tickets.view") || permissions.includes("tickets.staff_report.view");
}

export function hasStaffPortalAccess(flags: PortalFeatureFlags): boolean {
  return flags.showModTools || flags.showTicketTools || flags.showAdminTools || flags.showOwnerTools || flags.showStoreTools
    || flags.showFinanceTools || flags.showEventTools;
}

export function portalPageFromPath(pathname: string): PortalPage {
  const path = pathname.replace(/\/+$/, "").toLowerCase();
  if (path.endsWith("/map")) return "map";
  if (path.endsWith("/leaderboards") || path.endsWith("/records")) return "leaderboards";
  if (path.endsWith("/skins")) return "skins";
  if (path.endsWith("/garage")) return "garage";
  if (path.endsWith("/store")) return "store";
  if (path.endsWith("/marketplace")) return "marketplace";
  if (path.endsWith("/events")) return "events";
  if (path.endsWith("/gambling")) return "gambling";
  if (path.endsWith("/support")) return "support";
  if (path.endsWith("/cases")) return "cases";
  if (path.endsWith("/staff")) return "staff";
  if (path.endsWith("/senior")) return "senior";
  if (path.endsWith("/community")) return "community";
  if (path.endsWith("/technical")) return "technical";
  if (path.endsWith("/developer")) return "owner";
  if (path.endsWith("/config") || path.endsWith("/owner")) return "owner";
  return "overview";
}
